import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Loader2Icon } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useClickAction } from "@/app/[lang]/_hooks/use-click-action";
import { useServerActionErrorHandler } from "@/app/[lang]/_over-capacity";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useRouter } from "@/lib/navigation";
import { useRefreshWorkspaceSidebar } from "../../_contexts/workspace-sidebar-data-context";
import { duplicateProject } from "../../action";

interface DuplicateProjectDialogProps {
  trigger?: React.ReactNode;
  workspaceId: string;
  projectId: string;
  projectType: "iw" | "sa" | "ua" | "ads";
  projectName: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function DuplicateProjectDialog({
  trigger,
  workspaceId,
  projectId,
  projectType,
  projectName,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: DuplicateProjectDialogProps) {
  const { handleServerActionError } = useServerActionErrorHandler();
  const router = useRouter();
  const { i18n } = useLingui();
  const refreshWorkspaceSidebar = useRefreshWorkspaceSidebar();

  // Dialog state - use controlled props if provided, otherwise internal state
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledOnOpenChange || setInternalOpen;

  const [state, handleAction, isPending] = useClickAction(
    async () => {
      const result = await duplicateProject(projectId);

      if (!result) {
        return { errors: [i18n._(msg`Failed to duplicate project`)] };
      }

      // Over Capacityエラーをチェックしてモーダル表示
      if (result.errors && result.errors.length > 0) {
        handleServerActionError(result);
      }

      // プロジェクトが正常に複製された場合
      if (result.projectId) {
        // プロジェクトページへ遷移（設定画面ではなく一覧画面へ）
        const projectPath = `/${encodeURIComponent(workspaceId)}/${projectType}/${encodeURIComponent(result.projectId)}`;
        router.push(projectPath);
        router.refresh();

        // 成功メッセージを表示
        toast.success(i18n._(msg`Project duplicated`));

        // ダイアログを閉じる
        setOpen(false);
        refreshWorkspaceSidebar("project-mutation");
      }

      return result;
    },
    { errors: [] },
  );
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="max-w-md" onClose={() => setOpen(false)}>
        <DialogHeader>
          <DialogTitle>
            <Trans>Duplicate Project</Trans>
          </DialogTitle>
        </DialogHeader>
        <DialogDescription className="break-all overflow-hidden hyphens-auto">
          <Trans>
            Duplicate <span className="break-all">{projectName}</span>. All
            settings will be transferred to the new project.
          </Trans>
        </DialogDescription>
        {state && state.errors.length > 0 ? (
          <Alert variant="destructive">
            <AlertTitle>
              <Trans>Error</Trans>
            </AlertTitle>
            <AlertDescription>
              {state.errors.map((error) => (
                <div key={error}>{error}</div>
              ))}
            </AlertDescription>
          </Alert>
        ) : null}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">
              <Trans>Cancel</Trans>
            </Button>
          </DialogClose>
          <Button onClick={handleAction} disabled={isPending}>
            {isPending ? (
              <Loader2Icon className="w-4 h-4 animate-spin" />
            ) : (
              <Trans>Duplicate</Trans>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
